/**
 * Geographic Constants for BiteWise Healthcare Platform
 *
 * Centralized location for all geographic data including Australian states,
 * territories, and related geographic information.
 */

// =============================================================================
// AUSTRALIAN STATES AND TERRITORIES
// =============================================================================

/**
 * Australian state and territory codes
 * Maintains backward compatibility with existing string array usage
 */
export const AUSTRALIAN_STATES: readonly string[] = [
  'NSW',  // New South Wales
  'VIC',  // Victoria
  'QLD',  // Queensland
  'WA',   // Western Australia
  'SA',   // South Australia
  'TAS',  // Tasmania
  'ACT',  // Australian Capital Territory
  'NT'    // Northern Territory
] as const;

/**
 * Type for Australian state codes
 * Provides compile-time type safety for state operations
 */
export type AustralianStateCode = typeof AUSTRALIAN_STATES[number];

/**
 * Enhanced Australian state information interface
 * Supports future extensibility with additional metadata
 */
export interface AustralianStateInfo {
  readonly code: AustralianStateCode;
  readonly name: string;
  readonly type: 'state' | 'territory';
  readonly capital: string;
  readonly timezone: string;
  readonly population?: number;
  readonly area?: number;
}

/**
 * Complete Australian state and territory data
 * Provides rich metadata for enhanced functionality
 */
export const AUSTRALIAN_STATE_DATA: readonly AustralianStateInfo[] = [
  {
    code: 'NSW',
    name: 'New South Wales',
    type: 'state',
    capital: 'Sydney',
    timezone: 'AEST',
    population: 8166369,
    area: 800642
  },
  {
    code: 'VIC',
    name: 'Victoria',
    type: 'state',
    capital: 'Melbourne',
    timezone: 'AEST',
    population: 6681000,
    area: 227416
  },
  {
    code: 'QLD',
    name: 'Queensland',
    type: 'state',
    capital: 'Brisbane',
    timezone: 'AEST',
    population: 5206400,
    area: 1730648
  },
  {
    code: 'WA',
    name: 'Western Australia',
    type: 'state',
    capital: 'Perth',
    timezone: 'AWST',
    population: 2667130,
    area: 2529875
  },
  {
    code: 'SA',
    name: 'South Australia',
    type: 'state',
    capital: 'Adelaide',
    timezone: 'ACST',
    population: 1771703,
    area: 983482
  },
  {
    code: 'TAS',
    name: 'Tasmania',
    type: 'state',
    capital: 'Hobart',
    timezone: 'AEST',
    population: 541965,
    area: 68401
  },
  {
    code: 'ACT',
    name: 'Australian Capital Territory',
    type: 'territory',
    capital: 'Canberra',
    timezone: 'AEST',
    population: 431215,
    area: 2358
  },
  {
    code: 'NT',
    name: 'Northern Territory',
    type: 'territory',
    capital: 'Darwin',
    timezone: 'ACST',
    population: 249129,
    area: 1349129
  }
] as const;

// =============================================================================
// UTILITY FUNCTIONS
// =============================================================================

/**
 * Get state information by state code
 * @param code - Australian state/territory code
 * @returns State information or undefined if not found
 */
export const getStateByCode = (code: string): AustralianStateInfo | undefined => {
  return AUSTRALIAN_STATE_DATA.find(state => state.code === code);
};

/**
 * Validate if a code is a valid Australian state/territory
 * @param code - Code to validate
 * @returns True if valid Australian state/territory code
 */
export const isValidAustralianState = (code: string): code is AustralianStateCode => {
  return AUSTRALIAN_STATES.includes(code as AustralianStateCode);
};

/**
 * Get all Australian states (excluding territories)
 * @returns Array of state information for states only
 */
export const getAustralianStates = (): AustralianStateInfo[] => {
  return AUSTRALIAN_STATE_DATA.filter(item => item.type === 'state');
};

/**
 * Get all Australian territories
 * @returns Array of state information for territories only
 */
export const getAustralianTerritories = (): AustralianStateInfo[] => {
  return AUSTRALIAN_STATE_DATA.filter(item => item.type === 'territory');
};

/**
 * Format state code for display
 * @param code - State code
 * @param format - Display format ('code' | 'name' | 'full')
 * @returns Formatted state string
 */
export const formatStateDisplay = (
  code: string,
  format: 'code' | 'name' | 'full' = 'code'
): string => {
  const state = getStateByCode(code);
  if (!state) return code;

  switch (format) {
    case 'name':
      return state.name;
    case 'full':
      return `${state.name} (${state.code})`;
    case 'code':
    default:
      return state.code;
  }
};

// =============================================================================
// FORM INTEGRATION HELPERS
// =============================================================================

/**
 * Generate select options for Australian states
 * Compatible with existing form components
 */
export const AUSTRALIAN_STATE_OPTIONS = [
  { value: '', label: 'Select your state/territory' },
  ...AUSTRALIAN_STATES.map(code => {
    const state = getStateByCode(code);
    return {
      value: code,
      label: state ? `${state.name} (${code})` : code
    };
  })
] as const;

/**
 * Generate grouped select options (states vs territories)
 * For enhanced form UX when needed
 */
export const AUSTRALIAN_STATE_GROUPED_OPTIONS = [
  {
    label: 'States',
    options: getAustralianStates().map(state => ({
      value: state.code,
      label: `${state.name} (${state.code})`
    }))
  },
  {
    label: 'Territories',
    options: getAustralianTerritories().map(territory => ({
      value: territory.code,
      label: `${territory.name} (${territory.code})`
    }))
  }
] as const;

// =============================================================================
// VALIDATION INTEGRATION
// =============================================================================

/**
 * Validation result interface for consistency
 */
export interface StateValidationResult {
  isValid: boolean;
  error?: string;
  normalizedCode?: string;
}

/**
 * Validate and normalize Australian state code
 * @param input - User input state code
 * @param options - Validation options
 * @returns Validation result with normalized code
 */
export const validateAustralianState = (
  input: string,
  options: {
    required?: boolean;
    errorMessage?: string;
    allowEmpty?: boolean;
  } = {}
): StateValidationResult => {
  const {
    required = true,
    errorMessage = 'Please select a valid state or territory',
    allowEmpty = false
  } = options;

  // Handle empty input
  if (!input || input.trim() === '') {
    if (required && !allowEmpty) {
      return {
        isValid: false,
        error: 'State/territory selection is required'
      };
    }
    return { isValid: true };
  }

  // Normalize input (uppercase, trim)
  const normalizedCode = input.trim().toUpperCase();

  // Validate against known states
  if (!isValidAustralianState(normalizedCode)) {
    return {
      isValid: false,
      error: errorMessage
    };
  }

  return {
    isValid: true,
    normalizedCode
  };
};

// =============================================================================
// COUNTRY CONSTANTS
// =============================================================================

/**
 * Country option interface for form compatibility
 */
export interface CountryOption {
  readonly value: string;
  readonly label: string;
}

/**
 * Country options (Australia only for now)
 * Standardized format with AU country code for consistency
 */
export const COUNTRY_OPTIONS: readonly CountryOption[] = [
  { value: 'AU', label: 'Australia' }
] as const;

/**
 * Type for country codes
 */
export type CountryCode = typeof COUNTRY_OPTIONS[number]['value'];

/**
 * Validate if a code is a valid country code
 * @param code - Code to validate
 * @returns True if valid country code
 */
export const isValidCountryCode = (code: string): code is CountryCode => {
  return COUNTRY_OPTIONS.some(country => country.value === code);
};

/**
 * Get country information by country code
 * @param code - Country code
 * @returns Country option or undefined if not found
 */
export const getCountryByCode = (code: string): CountryOption | undefined => {
  return COUNTRY_OPTIONS.find(country => country.value === code);
};
